@if (data(); as eligibility) {
  <div class="grid gap-4">
    <div
      class="flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm"
      [class.border-green-200]="eligibility.status === 'Eligible'"
      [class.bg-green-50]="eligibility.status === 'Eligible'"
      [class.text-green-800]="eligibility.status === 'Eligible'"
      [class.border-red-200]="eligibility.status === 'NotEligible'"
      [class.bg-red-50]="eligibility.status === 'NotEligible'"
      [class.text-red-800]="eligibility.status === 'NotEligible'"
      [class.border-slate-200]="eligibility.status === 'Pending'"
      [class.bg-slate-50]="eligibility.status === 'Pending'"
      [class.text-slate-700]="eligibility.status === 'Pending'"
      role="status"
    >
      <mat-icon class="!text-base">{{ eligibility.status === 'Eligible' ? 'check_circle' : eligibility.status === 'NotEligible' ? 'flag' : 'hourglass_empty' }}</mat-icon>
      <span>
        @switch (eligibility.status) {
          @case ('Eligible') {
            Member eligible. The member details are locked on this case
            @if (eligibility.member.lockedDate) {
              (since {{ eligibility.member.lockedDate | date: 'medium' }}).
            }
          }
          @case ('NotEligible') {
            Member not eligible. The case stays open and is flagged; record the decision again once it is resolved.
          }
          @default {
            Eligibility has not been decided yet. Edits are saved on the employee and update this snapshot until eligibility passes.
          }
        }
      </span>
    </div>

    <!-- The member as the case sees them: the snapshot of the employee (and dependant) -->
    <mat-card class="rounded-3xl border border-slate-200 p-4 shadow-lg shadow-slate-200/60">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h3 class="text-lg font-semibold text-slate-900">Member</h3>
        @if (canEdit() && !editingMember()) {
          <button mat-stroked-button type="button" (click)="editMember()">Edit details</button>
        }
      </div>

      @if (editingMember()) {
        <form [formGroup]="memberForm" (submit)="$event.preventDefault(); saveMember()" class="grid gap-4">
          <p class="text-sm text-slate-500">These are saved on the employee, and the bulk import will not overwrite the fields changed here.</p>
          <div class="grid gap-x-4 md:grid-cols-3">
            <mat-form-field appearance="outline">
              <mat-label>First name</mat-label>
              <input matInput maxlength="100" formControlName="firstName" />
              @if (memberForm.controls.firstName.hasError('required')) {
                <mat-error>First name is required.</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Surname</mat-label>
              <input matInput maxlength="100" formControlName="surname" />
              @if (memberForm.controls.surname.hasError('required')) {
                <mat-error>Surname is required.</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>ID number</mat-label>
              <input matInput maxlength="30" formControlName="idNumber" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Date of birth</mat-label>
              <input matInput type="date" formControlName="dateOfBirth" />
            </mat-form-field>
            <lookup-select formControlName="genderId" label="Gender" [options]="genders()" />
            <lookup-select formControlName="maritalStatusId" label="Marital status" [options]="maritalStatuses()" />
            <mat-form-field appearance="outline">
              <mat-label>Contact number</mat-label>
              <input matInput maxlength="50" formControlName="contactNumber" />
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Email address</mat-label>
              <input matInput type="email" maxlength="320" formControlName="emailAddress" />
              @if (memberForm.controls.emailAddress.hasError('email')) {
                <mat-error>Enter a valid email address.</mat-error>
              }
            </mat-form-field>
            <lookup-select formControlName="companyJobTitleId" label="Occupation" [options]="jobTitles()" />
            <lookup-select formControlName="level1Id" label="Business unit" [options]="levelOptions(1)" />
            <lookup-select formControlName="level2Id" label="Department" [options]="levelOptions(2)" />
            <lookup-select formControlName="level3Id" label="Level 3" [options]="levelOptions(3)" />
          </div>
          <div class="flex justify-end gap-2">
            <button mat-stroked-button type="button" (click)="editingMember.set(false)">Cancel</button>
            <button mat-flat-button type="submit" [disabled]="store.savingEligibility()">
              @if (store.savingEligibility()) {
                <mat-spinner diameter="20" class="mr-2" />
              }
              Save member details
            </button>
          </div>
        </form>
      } @else {
        <dl class="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2">
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Name</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberName }} {{ eligibility.member.memberSurname }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Member type</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberType || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Employee number</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberEmployeeNumber || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">ID number</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberIdNumber || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Date of birth</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberDateOfBirth || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Age at start</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.ageYearsAtStart ?? '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Gender</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberGender || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Marital status</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberMaritalStatus || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Contact number</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberMobileNumber || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Email address</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.memberEmailAddress || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Customer</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.clientName || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Occupation</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.companyJobTitle || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Business unit</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.level1 || '-' }}</dd>
          </div>
          <div class="flex gap-2">
            <dt class="w-40 shrink-0 text-slate-500">Department</dt>
            <dd class="font-medium text-slate-900">{{ eligibility.member.level2 || '-' }}</dd>
          </div>
          @if (eligibility.member.level3) {
            <div class="flex gap-2">
              <dt class="w-40 shrink-0 text-slate-500">Level 3</dt>
              <dd class="font-medium text-slate-900">{{ eligibility.member.level3 }}</dd>
            </div>
          }
        </dl>
      }
    </mat-card>

    <!-- The dependant, when the case is for one -->
    <eligibility-dependant-section [interventionId]="interventionId()" [member]="eligibility.member" [readOnly]="!canEdit()" />

    <!-- The Momentum products the member holds, verified on the call -->
    <mat-card class="rounded-3xl border border-slate-200 p-4 shadow-lg shadow-slate-200/60">
      <h3 class="mb-3 text-lg font-semibold text-slate-900">Momentum products</h3>
      <div class="grid gap-1 md:grid-cols-2">
        @for (item of momentumProducts(); track item.id) {
          <mat-checkbox [checked]="isProductChosen(item.id)" [disabled]="!canEdit()" (change)="toggleProduct(item, $event.checked)">{{ item.name }}</mat-checkbox>
        }
      </div>
      @if (canEdit()) {
        <div class="mt-3 flex justify-end">
          <button mat-stroked-button type="button" [disabled]="store.savingEligibility()" (click)="saveProducts()">Save products</button>
        </div>
      }
    </mat-card>

    <!-- The member's earlier interventions, so the clinician sees the history while on the call -->
    @if (eligibility.member.employeeId) {
      <employee-interventions-panel [employeeId]="eligibility.member.employeeId" />
    }

    <!-- The decision, with the audit of the checks -->
    <mat-card class="rounded-3xl border border-slate-200 p-4 shadow-lg shadow-slate-200/60">
      <h3 class="mb-3 text-lg font-semibold text-slate-900">Member eligible</h3>
      @if (canEdit()) {
        <form [formGroup]="decisionForm" (submit)="$event.preventDefault(); decide()" class="grid gap-3">
          <mat-radio-group formControlName="eligible" class="flex gap-6" aria-label="Member eligible">
            <mat-radio-button value="yes">Yes</mat-radio-button>
            <mat-radio-button value="no">No</mat-radio-button>
          </mat-radio-group>
          <mat-form-field appearance="outline">
            <mat-label>Note</mat-label>
            <textarea matInput rows="2" maxlength="1000" formControlName="detail"></textarea>
          </mat-form-field>
          <div class="flex justify-end">
            <button mat-flat-button type="submit" [disabled]="store.savingEligibility()">Record decision</button>
          </div>
        </form>
      }

      @if (eligibility.checks.length > 0) {
        <h4 class="mt-4 font-semibold text-slate-900">Checks</h4>
        <ul class="mt-2 grid gap-2 text-sm">
          @for (check of eligibility.checks; track check.id) {
            <li class="flex flex-wrap items-center gap-2">
              <span
                class="rounded-full px-2 py-0.5 text-xs font-semibold"
                [class.bg-green-100]="check.result === 'Pass'"
                [class.text-green-800]="check.result === 'Pass'"
                [class.bg-amber-100]="check.result === 'Warn'"
                [class.text-amber-800]="check.result === 'Warn'"
                [class.bg-red-100]="check.result === 'Fail'"
                [class.text-red-800]="check.result === 'Fail'"
                >{{ check.result }}</span
              >
              <span class="font-medium text-slate-900">{{ check.ruleName }}</span>
              <span class="text-slate-600">{{ check.detail }}</span>
              <span class="ml-auto text-xs text-slate-400">{{ check.checkedDate | date: 'short' }}</span>
            </li>
          }
        </ul>
      }
    </mat-card>
  </div>
}
